// StepThreeCheckout.jsx
import React, { useEffect, useState, Suspense, useRef } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import axiosInstance from '../../../../utils/axiosInstance';
import { getCheckoutDescription } from './namingHelpers';
import { FaArrowAltCircleLeft, FaCashRegister } from 'react-icons/fa';
import BillingAddressForm from './BillingAddressForm';
import Spinner from '../../../Spinner/Spinner';
import './StepThreeCheckout.css';

const CheckoutForm = React.lazy(() => import('../CheckoutForm/CheckoutForm'));

function StepThreeCheckout({
  stripePromise,
  onPaymentSuccess,
  onPaymentFailure,
  quantity,
  selectedOption,
  user,
  useSavedCard,
  setUseSavedCard,
  makeRecurring,
  onBack,
  productMapping,
}) {
  const [clientSecret, setClientSecret] = useState('');
  const [isLoadingIntent, setIsLoadingIntent] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useRef(null);
  const [basePrice, setBasePrice] = useState('');
  const [tax, setTax] = useState('');
  const [finalPrice, setFinalPrice] = useState('');
  const [savedAddress, setSavedAddress] = useState(null);
  const [useSavedAddress, setUseSavedAddress] = useState(false);
  const [billingAddress, setBillingAddress] = useState(null);
  const [shouldSaveAddress, setShouldSaveAddress] = useState(false);
  const [cardDetails, setCardDetails] = useState(null);
  const [isLoadingCard, setIsLoadingCard] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(true);
  const [addressSuccess, setAddressSuccess] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const paymentSectionRef = useRef(null);

  // Add useEffect to scroll to bottom of modal when payment section becomes visible
  useEffect(() => {
    if (clientSecret && !isLoadingIntent) {
      setTimeout(() => {
        const modalContent = document.querySelector('.buy-pass-modal-content');
        if (modalContent) {
          modalContent.scrollTo({
            top: modalContent.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [clientSecret, isLoadingIntent]);

  // Provide a description for what the user is about to purchase
  const description = getCheckoutDescription(selectedOption, quantity, makeRecurring);

  // Fetch saved address on mount
  useEffect(() => {
    const fetchSavedAddress = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axiosInstance.get('/users/billing-address', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.address) {
          setSavedAddress(response.data.address);
          setBillingAddress(response.data.address);
          setUseSavedAddress(true);
        }
      } catch (err) {
        console.error('Error fetching saved address:', err);
      }
    };

    if (user) {
      fetchSavedAddress();
    }
  }, [user]);

  // Fetch saved card details
  useEffect(() => {
    const fetchCardDetails = async () => {
      if (!user?.stripeCardId) {
        return;
      }
      
      setIsLoadingCard(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axiosInstance.get('/users/card-details', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCardDetails(response.data.card);
      } catch (err) {
        console.error('Error fetching card details:', err);
      } finally {
        setIsLoadingCard(false);
      }
    };

    fetchCardDetails();
  }, [user]);

  // Set useSavedCard to true by default if user has a saved card
  useEffect(() => {
    if (user?.stripeCardId) {
      setUseSavedCard(true);
    }
  }, [user, setUseSavedCard]);

  // Force card saving for recurring payments
  useEffect(() => {
    if (makeRecurring) {
      // Don't force useSavedCard to false, just ensure the card will be used for recurring payments
      if (user?.stripeCardId) {
        setUseSavedCard(true);
      }
    }
  }, [makeRecurring, user?.stripeCardId]);

  // Add useEffect to scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      // Small delay to ensure the error message is rendered
      setTimeout(() => {
        errorRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center'  // This will center the error message in the viewport
        });
        // Add extra padding by scrolling up a bit more
        window.scrollBy({
          top: -50,  // Scroll up by 50 pixels
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [error]);

  const createPaymentIntent = async () => {
    setIsLoadingIntent(true);
    setError('');  // Clear error first

    try {
      // Validate billing address before proceeding
      if (!billingAddress || !billingAddress.line1 || !billingAddress.city || !billingAddress.state || !billingAddress.postal_code) {
        await new Promise(resolve => setTimeout(resolve, 0));  // Wait for next tick
        setError('Please provide a complete billing address to continue.');
        setIsLoadingIntent(false);
        return null;
      }

      const isMonthly = (selectedOption === 'Monthly Pass');
      let endpoint = '/payment/create-payment-intent';

      // Build the body to send to the server
      const bodyData = { 
        priceId: '', 
        quantity, 
        recurring: false,
        address: {
          line1: billingAddress.line1,
          line2: billingAddress.line2 || undefined,
          city: billingAddress.city,
          state: billingAddress.state,
          postal_code: billingAddress.postal_code,
          country: billingAddress.country || 'US'
        }
      };

      // Get product mapping for the selected option
      const productInfo = productMapping[selectedOption];
      if (!productInfo) {
        throw new Error(`No product mapping found for option: ${selectedOption}`);
      }

      // Determine which price or product ID should be used
      if (selectedOption === 'Monthly Pass' && makeRecurring) {
        endpoint = '/payment/create-subscription-intent';
        bodyData.priceId = productInfo.recurringProductId;
        bodyData.recurring = true;
        bodyData.enableTax = true;
      } else {
        bodyData.priceId = productInfo.productId;
      }

      const token = localStorage.getItem('token');
      const response = await axiosInstance.post(endpoint, bodyData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data) {
        // Handle both payment and subscription intents
        if (response.data.clientSecret) {
          setClientSecret(response.data.clientSecret);
        } else if (response.data.subscriptionId) {
          setClientSecret(response.data.subscriptionId);
        } else {
          throw new Error('No client secret or subscription ID found in response');
        }

        // Handle price information
        if (response.data.taxInclusivePrice) {
          const serverTotal = response.data.taxInclusivePrice / 100;
          const base = response.data.basePrice / 100;
          const tax = response.data.taxAmount / 100;

          setBasePrice(base.toFixed(2));
          setTax(tax.toFixed(2));
          setFinalPrice(serverTotal.toFixed(2));
        } else if (response.data.amount) {
          const serverTotal = response.data.amount / 100;
          const base = serverTotal / 1.06;
          const taxAmount = serverTotal - base;

          setBasePrice(base.toFixed(2));
          setTax(taxAmount.toFixed(2));
          setFinalPrice(serverTotal.toFixed(2));
        } else {
          setError('Price information is not available yet. Please try again in a moment.');
          setBasePrice('');
          setTax('');
          setFinalPrice('');
        }

        return response.data;
      }
    } catch (err) {
      console.error('Error creating payment intent:', err);
      await new Promise(resolve => setTimeout(resolve, 0));  // Wait for next tick
      setError('Failed to create payment intent. Please try again.');
      return null;
    } finally {
      setIsLoadingIntent(false);
    }
  };

  const handleAddressChange = async (address) => {
    // Validate the address before setting it
    if (!address || !address.line1 || !address.city || !address.state || !address.postal_code) {
      await new Promise(resolve => setTimeout(resolve, 0));  // Wait for next tick
      setError('Please provide a complete billing address.');
      return;
    }

    setBillingAddress(address);
    setShowAddressForm(false);
    setAddressSuccess(true);
    setIsEditingAddress(false);
    
    // Scroll to payment section
    setTimeout(() => {
      paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    
    // If user wants to save the address, send it to the backend
    if (shouldSaveAddress) {
      try {
        const token = localStorage.getItem('token');
        await axiosInstance.post('/users/billing-address', address, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSavedAddress(address);
      } catch (err) {
        console.error('Error saving address:', err);
        await new Promise(resolve => setTimeout(resolve, 0));  // Wait for next tick
        setError('Failed to save address. Please try again.');
      }
    }
  };

  // Function to handle closing the form without saving
  const handleCloseForm = () => {
    setShowAddressForm(false);
    setAddressSuccess(false);
    setIsEditingAddress(false);
    setBillingAddress(null);
  };

  return (
    <div style={{ padding: '1em' }}>
      <h3>Checkout</h3>

      {/* Display any errors from creating payment/subscription intents */}
      {error && (
        <p ref={errorRef} style={{ color: 'red', marginBottom: '1em' }}>
          {error}
        </p>
      )}

      <p>
        You're about to buy: <strong>{description}</strong>
      </p>

      {/* Billing Address Section */}
      <div className="billing-address-section">
        <h4>Billing Address</h4>
        
        {savedAddress && savedAddress.line1 ? (
          <div className="saved-address-option">
            <label>
              <input
                type="checkbox"
                checked={useSavedAddress}
                onChange={(e) => {
                  setUseSavedAddress(e.target.checked);
                  if (e.target.checked) {
                    setBillingAddress(savedAddress);
                    setShowAddressForm(false);
                    setAddressSuccess(true);
                    setIsEditingAddress(false);
                  } else {
                    setBillingAddress(null);
                    setShowAddressForm(true);
                    setAddressSuccess(false);
                    setIsEditingAddress(false);
                  }
                }}
              />
              Use saved billing address
            </label>
            {useSavedAddress && (
              <div className="saved-address-display">
                <p>{savedAddress.line1}</p>
                {savedAddress.line2 && <p>{savedAddress.line2}</p>}
                <p>{savedAddress.city}, {savedAddress.state} {savedAddress.postal_code}</p>
                {savedAddress.country && <p>{savedAddress.country}</p>}
              </div>
            )}
          </div>
        ) : null}

        {(!savedAddress || !savedAddress.line1 || !useSavedAddress) && (
          <>
            {showAddressForm ? (
              <BillingAddressForm
                savedAddress={isEditingAddress ? billingAddress : null}
                onAddressChange={handleAddressChange}
                onSaveAddressChange={setShouldSaveAddress}
                showSaveAddressOption={!savedAddress || !savedAddress.line1}
                forceSaveAddress={makeRecurring}
                onClose={handleCloseForm}
              />
            ) : addressSuccess && (
              <div className="address-success-message">
                <p>✓ Billing address accepted</p>
                <button 
                  className="edit-address-button"
                  onClick={() => {
                    setShowAddressForm(true);
                    setAddressSuccess(false);
                    setIsEditingAddress(true);
                  }}
                >
                  Edit Address
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Payment Method Section */}
      <div ref={paymentSectionRef}>
        {user?.stripeCardId && (
          <div className="payment-method-section">
            <h4>Payment Method</h4>
            <div className="saved-card-option">
              <label>
                <input
                  type="checkbox"
                  checked={useSavedCard}
                  onChange={(e) => setUseSavedCard(e.target.checked)}
                />
                Use saved card on file
              </label>
              {useSavedCard && cardDetails && (
                <div className="saved-card-details">
                  <p>{cardDetails.brand.charAt(0).toUpperCase() + cardDetails.brand.slice(1)} ending in {cardDetails.last4}</p>
                  <p>Expires {cardDetails.exp_month}/{cardDetails.exp_year}</p>
                </div>
              )}
              {useSavedCard && makeRecurring && (
                <div className="saved-card-notice">
                  <p><strong>Note:</strong> This card will be used for automatic monthly renewals.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: '1em' }}>
        <button onClick={onBack} className="StepThreeBackButton">
          <FaArrowAltCircleLeft className="StepThreeBackButtonIcon" />
          Back
        </button>

        {/* Only show price details and payment form after payment intent is created */}
        {isLoadingIntent ? (
          <div className="loading-container">
            <Spinner />
            {!billingAddress && (
              <p>Add your billing address to continue with checkout</p>
            )}
          </div>
        ) : clientSecret && stripePromise && billingAddress ? (
          <>
            <div className="price-details">
              <p>Base Price: ${basePrice}</p>
              <p>Tax: ${tax}</p>
              <p>Total: ${finalPrice}</p>
            </div>
            <Suspense fallback={
              <div className="loading-container">
                <Spinner />
                <p>Loading payment details...</p>
              </div>
            }>
              <Elements stripe={stripePromise} options={{ 
                clientSecret,
                paymentMethodCreation: 'manual'
              }}>
                <CheckoutForm
                  savedCardId={user?.stripeCardId}
                  useSavedCard={useSavedCard}
                  setUseSavedCard={setUseSavedCard}
                  clientSecret={clientSecret}
                  onPaymentSuccess={() => onPaymentSuccess(finalPrice)}
                  onPaymentFailure={onPaymentFailure}
                  makeRecurring={makeRecurring}
                  showNewCardOption={!useSavedCard}
                />
              </Elements>
            </Suspense>
          </>
        ) : (
          <div className="loading-container">
            <button 
              onClick={createPaymentIntent}
              disabled={!billingAddress || isLoadingIntent}
              className="CheckoutFormPayButton"
            >
              <FaCashRegister className="cash-register-icon" />
              Continue to Payment
            </button>
            {!billingAddress && (
              <p>Add your billing address to continue with checkout</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default StepThreeCheckout;
